import React, { useState, useEffect } from 'react';
import "../../css/Students.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaSearch, FaUserGraduate, FaEdit, FaTrash, FaFilter, FaTimes } from 'react-icons/fa';

function Students() {
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOption, setFilterOption] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentStudent, setCurrentStudent] = useState(null);
    const [editFormData, setEditFormData] = useState({});


    useEffect(() => {
        fetchStudents();
    }, []);



    const fetchStudents = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/students", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch students");
            }

            const data = await response.json();
            setStudents(data);
            setFilteredStudents(data);
        } catch (error) {
            console.error("Error fetching students:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };


    // Handle opening the edit modal
    const handleEditClick = (student) => {
        setCurrentStudent(student);
        setEditFormData({ ...student });
        setShowEditModal(true);
    };

    // Handle opening the delete modal
    const handleDeleteClick = (student) => {
        setCurrentStudent(student);
        setShowDeleteModal(true);
    };


    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData({
            ...editFormData,
            [name]: value
        });
    };

    // Handle form submission for update
    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/students/${currentStudent._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update student");
            }

            const data = await response.json();

            // Update the students array with updated data
            const updatedStudents = students.map(student =>
                student._id === currentStudent._id ? data.student : student
            );

            setStudents(updatedStudents);
            setFilteredStudents(updatedStudents);

            toast.success("Student updated successfully!");
            setShowEditModal(false);
        } catch (error) {
            console.error("Error updating student:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };
    // Handle student deletion
    const handleDeleteConfirm = async () => {
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/students/${currentStudent._id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete student");
            }

            // Remove the deleted student from state
            const updatedStudents = students.filter(student => student._id !== currentStudent._id);
            setStudents(updatedStudents);
            setFilteredStudents(updatedStudents);

            toast.success("Student deleted successfully!");
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Error deleting student:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search and filtering
    useEffect(() => {
        let results = students;

        // Apply search
        if (searchTerm) {
            results = results.filter(student =>
                (student.firstName?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (student.lastName?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (student.rollNumber?.toString() || "").includes(searchTerm) ||
                (student.grade?.toString() || "").includes(searchTerm) ||
                (student.email?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase())
            );
        }

        // Apply filters
        if (filterOption !== 'all') {
            if (filterOption === 'male' || filterOption === 'female') {
                results = results.filter(student =>
                    student.gender?.toString().toLowerCase() === filterOption
                );
            } else {
                // Filter by grade
                results = results.filter(student =>
                    student.grade?.toString() === filterOption
                );
            }
        }

        setFilteredStudents(results);
    }, [searchTerm, filterOption, students]);

    // Get unique grades for filter dropdown
    const grades = [...new Set(students.map(student => student.grade?.toString()))].filter(Boolean);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterChange = (e) => {
        setFilterOption(e.target.value);
    };

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    // Format date safely
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleDateString();
        } catch (e) {
            return dateString;
        }
    };
    // Format date for input field
    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        } catch (e) {
            return "";
        }
    };

    return (
        <div className="students-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="students-header">
                <div className="students-title-container">
                    <FaUserGraduate className="students-icon" />
                    <h1 className="students-title">Student Directory</h1>
                </div>

                <div className="students-controls">
                    <div className="search-container">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by name, roll number, email..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>

                    <button className="filter-toggle-btn" onClick={toggleFilters}>
                        <FaFilter /> Filters
                    </button>
                </div>

                {showFilters && (
                    <div className="filter-options">
                        <select
                            value={filterOption}
                            onChange={handleFilterChange}
                            className="filter-select"
                        >
                            <option value="all">All Students</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            {grades.length > 0 && (
                                <optgroup label="Grades">
                                    {grades.map(grade => (
                                        <option key={grade} value={grade}>Grade {grade}</option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                    </div>
                )}

                <div className="students-stats">
                    <div className="stat-box">
                        <span className="stat-number">{filteredStudents.length}</span>
                        <span className="stat-label">Students</span>
                    </div>
                    {filterOption !== 'all' && (
                        <div className="stat-box highlight">
                            <span className="stat-number">{filterOption}</span>
                            <span className="stat-label">{grades.includes(filterOption) ? 'Grade' : 'Gender'}</span>
                        </div>
                    )}
                </div>
            </div>

            {filteredStudents.length === 0 && !isLoading ? (
                <div className="students-empty">
                    <div className="empty-icon">
                        <FaSearch size={48} color="#a0aec0" />
                    </div>
                    <p className="empty-text">No students found matching your criteria.</p>
                    {searchTerm && (
                        <button className="reset-search" onClick={() => setSearchTerm('')}>
                            Clear Search
                        </button>
                    )}
                </div>
            ) : (
                <div className="students-grid">
                    {filteredStudents.map((student, index) => {
                        // Get first character of names safely
                        const firstInitial = student.firstName ? student.firstName.charAt(0) : '';
                        const lastInitial = student.lastName ? student.lastName.charAt(0) : '';
                        const gender = student.gender?.toString().toLowerCase() || 'other';

                        return (
                            <div key={index} className="student-card">
                                <div className={`student-card-header ${gender === 'female' ? 'female' : 'male'}`}>
                                    <div className="student-avatar">
                                        {firstInitial}{lastInitial}
                                    </div>
                                    <div className="student-header-info">
                                        <h2 className="student-name">
                                            {student.firstName || ''} {student.lastName || ''}
                                        </h2>
                                        <span className="student-roll">
                                            #{student.rollNumber || 'N/A'}
                                        </span>
                                    </div>
                                </div>

                                <div className="student-card-body">
                                    <div className="student-quick-info">
                                        <span className="grade-badge">Grade {student.grade || 'N/A'}-{student.section || ''}</span>
                                        <span className="gender-badge">{student.gender || 'N/A'}</span>
                                    </div>

                                    <div className="student-info-section">
                                        <h3 className="section-title">Personal Info</h3>
                                        <div className="student-info-group">
                                            <span className="student-info-label">Email:</span>
                                            <span className="student-info-value">{student.email || 'N/A'}</span>
                                        </div>
                                        <div className="student-info-group">
                                            <span className="student-info-label">Phone:</span>
                                            <span className="student-info-value">{student.phone || 'N/A'}</span>
                                        </div>
                                        <div className="student-info-group">
                                            <span className="student-info-label">DOB:</span>
                                            <span className="student-info-value">{student.dob || 'N/A'}</span>
                                        </div>
                                        {student.bloodGroup && (
                                            <div className="student-info-group">
                                                <span className="student-info-label">Blood Group:</span>
                                                <span className="student-info-value blood-group">{student.bloodGroup}</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="student-info-section">
                                        <h3 className="section-title">Address</h3>
                                        <div className="student-info-group address">
                                            <span className="student-info-value">{student.address || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="student-info-section">
                                        <h3 className="section-title">Parent Details</h3>
                                        <div className="student-info-group">
                                            <span className="student-info-label">Name:</span>
                                            <span className="student-info-value">{student.parentName || 'N/A'}</span>
                                        </div>
                                        <div className="student-info-group">
                                            <span className="student-info-label">Phone:</span>
                                            <span className="student-info-value">{student.parentPhone || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="student-info-group admission-date">
                                        <span className="student-info-label">Admission:</span>
                                        <span className="student-info-value">{formatDate(student.admissionDate)}</span>
                                    </div>
                                </div>

                                <div className="student-card-footer">
                                    <button className="student-action-btn edit-btn" onClick={() => handleEditClick(student)}>
                                        <FaEdit /> Edit
                                    </button>
                                    <button className="student-action-btn delete-btn" onClick={() => handleDeleteClick(student)}>
                                        <FaTrash /> Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {/* Edit Modal */}
            {showEditModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h2>Edit Student</h2>
                            <button className="close-btn" onClick={() => setShowEditModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleUpdateSubmit}>
                                <div className="form-group">
                                    <label>First Name:</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        value={editFormData.firstName || ''}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Name:</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        value={editFormData.lastName || ''}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Roll Number:</label>
                                    <input
                                        type="text"
                                        name="rollNumber"
                                        value={editFormData.rollNumber || ''}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group half">
                                        <label>Grade:</label>
                                        <input
                                            type="text"
                                            name="grade"
                                            value={editFormData.grade || ''}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                    <div className="form-group half">
                                        <label>Section:</label>
                                        <input
                                            type="text"
                                            name="section"
                                            value={editFormData.section || ''}
                                            onChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Gender:</label>
                                    <select
                                        name="gender"
                                        value={editFormData.gender || ''}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Email:</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={editFormData.email || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Phone:</label>
                                    <input
                                        type="text"
                                        name="phone"
                                        value={editFormData.phone || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Date of Birth:</label>
                                    <input
                                        type="date"
                                        name="dob"
                                        value={formatDateForInput(editFormData.dob)}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Blood Group:</label>
                                    <input
                                        type="text"
                                        name="bloodGroup"
                                        value={editFormData.bloodGroup || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Address:</label>
                                    <textarea
                                        name="address"
                                        value={editFormData.address || ''}
                                        onChange={handleInputChange}
                                    ></textarea>
                                </div>
                                <div className="form-group">
                                    <label>Parent Name:</label>
                                    <input
                                        type="text"
                                        name="parentName"
                                        value={editFormData.parentName || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Parent Phone:</label>
                                    <input
                                        type="text"
                                        name="parentPhone"
                                        value={editFormData.parentPhone || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Admission Date:</label>
                                    <input
                                        type="date"
                                        name="admissionDate"
                                        value={formatDateForInput(editFormData.admissionDate)}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="cancel-btn" onClick={() => setShowEditModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="save-btn">
                                        Update Student
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-container delete-modal">
                        <div className="modal-header">
                            <h2>Delete Student</h2>
                            <button className="close-btn" onClick={() => setShowDeleteModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="delete-warning">
                                <FaTrash className="delete-icon" />
                                <p>Are you sure you want to delete {currentStudent?.firstName} {currentStudent?.lastName}?</p>
                                <p className="warning-text">This action cannot be undone.</p>
                            </div>
                            <div className="modal-footer">
                                <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>
                                    Cancel
                                </button>
                                <button className="delete-confirm-btn" onClick={handleDeleteConfirm}>
                                    Delete Student
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Students;